import {
  buildCognitoryWorkspaceCapabilitiesPath,
  resolveCognitoryApiBaseUrl,
} from "./cognitoryApiBase";
import { resolveCognitoryBearerToken } from "./cognitoryBearerToken";
import {
  rebuildCognitoryWorkspaceCapabilitiesPayload,
  type CognitoryWorkspaceCapabilitiesView,
} from "./cognitoryWorkspaceCapabilitiesRebuild";

export type CognitoryWorkspaceCapabilities = CognitoryWorkspaceCapabilitiesView;

export type CognitoryWorkspaceCapabilitiesClientResult =
  | {
      kind: "resolved";
      requestId: string;
      cognitoryId: string;
      capabilities: CognitoryWorkspaceCapabilities;
    }
  | {
      kind:
        | "unauthenticated"
        | "forbidden"
        | "invalid_request"
        | "temporary_failure"
        | "malformed_success";
      requestId: string | null;
    };

export async function fetchCognitoryWorkspaceCapabilities(input: {
  cognitoryId: string;
  apiBaseUrl?: string;
  bearerToken?: string | null;
  fetchImpl?: typeof fetch;
}): Promise<CognitoryWorkspaceCapabilitiesClientResult> {
  const fetchImpl = input.fetchImpl ?? fetch;
  const token =
    input.bearerToken !== undefined
      ? input.bearerToken
      : await resolveCognitoryBearerToken();
  if (!token) {
    return { kind: "unauthenticated", requestId: null };
  }
  const base = input.apiBaseUrl ?? resolveCognitoryApiBaseUrl();
  const path = buildCognitoryWorkspaceCapabilitiesPath(input.cognitoryId);
  let response: Response;
  try {
    response = await fetchImpl(`${base}${path}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
      cache: "no-store",
    });
  } catch {
    return { kind: "temporary_failure", requestId: null };
  }
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return { kind: "malformed_success", requestId: null };
  }
  if (response.status === 401) {
    const requestId =
      body &&
      typeof body === "object" &&
      typeof (body as { requestId?: unknown }).requestId === "string"
        ? (body as { requestId: string }).requestId
        : null;
    return { kind: "unauthenticated", requestId };
  }
  if (response.status === 403) {
    const requestId =
      body &&
      typeof body === "object" &&
      typeof (body as { requestId?: unknown }).requestId === "string"
        ? (body as { requestId: string }).requestId
        : null;
    return { kind: "forbidden", requestId };
  }
  if (response.status === 400) {
    const requestId =
      body &&
      typeof body === "object" &&
      typeof (body as { requestId?: unknown }).requestId === "string"
        ? (body as { requestId: string }).requestId
        : null;
    return { kind: "invalid_request", requestId };
  }
  if (response.status !== 200) {
    const requestId =
      body &&
      typeof body === "object" &&
      typeof (body as { requestId?: unknown }).requestId === "string"
        ? (body as { requestId: string }).requestId
        : null;
    return { kind: "temporary_failure", requestId };
  }
  const rebuilt = rebuildCognitoryWorkspaceCapabilitiesPayload({
    expectedCognitoryId: input.cognitoryId,
    body,
  });
  if (rebuilt.kind !== "success") {
    return {
      kind: "malformed_success",
      requestId: rebuilt.supportReference,
    };
  }
  return {
    kind: "resolved",
    requestId: rebuilt.supportReference,
    cognitoryId: rebuilt.cognitoryId,
    capabilities: rebuilt.capabilities,
  };
}
